import type { SoundEffectGenerationTask } from '@/stores/soundEffectGenerationStore';
import type { VoiceGenerationTask } from '@/stores/voiceGenerationStore';

export interface RemoteVoiceoverSnapshot {
  voiceoverId: string;
  dialogueId: string;
  audioUrl?: string | null;
}

export interface RemoteSoundEffectSnapshot {
  soundEffectId: string;
  audioUrl?: string | null;
}

function normalizeAudioUrl(value?: string | null): string {
  return value?.trim() ?? '';
}

export function getResolvedVoiceoverTaskIds(
  tasks: VoiceGenerationTask[],
  remoteVoiceovers: RemoteVoiceoverSnapshot[]
): string[] {
  const taskIdsToRemove = new Set<string>();
  const remoteByDialogueId = new Map<string, RemoteVoiceoverSnapshot[]>();

  remoteVoiceovers.forEach((voiceover) => {
    const current = remoteByDialogueId.get(voiceover.dialogueId) ?? [];
    current.push(voiceover);
    remoteByDialogueId.set(voiceover.dialogueId, current);
  });

  const tasksByDialogueId = new Map<string, VoiceGenerationTask[]>();
  tasks.forEach((task) => {
    if (task.status !== 'completed') return;
    const current = tasksByDialogueId.get(task.dialogueId) ?? [];
    current.push(task);
    tasksByDialogueId.set(task.dialogueId, current);
  });

  tasksByDialogueId.forEach((dialogueTasks, dialogueId) => {
    const remoteItems = remoteByDialogueId.get(dialogueId) ?? [];
    const remoteAudioUrls = new Set(remoteItems.map((item) => normalizeAudioUrl(item.audioUrl)));
    const consumedRemoteIds = new Set<string>();

    dialogueTasks.forEach((task) => {
      if (!task.audioUrl) return;
      if (remoteAudioUrls.has(normalizeAudioUrl(task.audioUrl))) {
        taskIdsToRemove.add(task.localId);
      }
    });

    dialogueTasks
      .filter((task) => !task.audioUrl && !taskIdsToRemove.has(task.localId))
      .sort((a, b) => a.createdAt - b.createdAt)
      .forEach((task) => {
        const replacement = remoteItems.find(
          (item) =>
            !task.initialRemoteVoiceoverIds.includes(item.voiceoverId) &&
            !consumedRemoteIds.has(item.voiceoverId)
        );
        if (!replacement) return;
        consumedRemoteIds.add(replacement.voiceoverId);
        taskIdsToRemove.add(task.localId);
      });
  });

  return Array.from(taskIdsToRemove);
}

/**
 * 画布音效组占位：尚未被远端列表「消化」的本地生成任务（含 pending/processing/未匹配 completed）。
 * 与 SoundEffectPreview.previewTasks 口径一致。
 */
export function getDisplayableSoundEffectLocalTasks(
  tasks: SoundEffectGenerationTask[],
  remoteSoundEffects: RemoteSoundEffectSnapshot[]
): SoundEffectGenerationTask[] {
  const remoteAudioUrls = new Set(
    remoteSoundEffects.map((item) => normalizeAudioUrl(item.audioUrl)).filter(Boolean)
  );
  return [...tasks]
    .filter((task) => {
      if (!task.audioUrl) return true;
      return !remoteAudioUrls.has(normalizeAudioUrl(task.audioUrl));
    })
    .sort((a, b) => b.createdAt - a.createdAt);
}

export function getResolvedSoundEffectTaskIds(
  tasks: SoundEffectGenerationTask[],
  remoteSoundEffects: RemoteSoundEffectSnapshot[]
): string[] {
  const taskIdsToRemove = new Set<string>();
  const remoteAudioUrls = new Set(
    remoteSoundEffects.map((item) => normalizeAudioUrl(item.audioUrl))
  );
  const consumedRemoteIds = new Set<string>();

  tasks
    .filter((task) => task.status === 'completed')
    .forEach((task) => {
      if (!task.audioUrl) return;
      if (remoteAudioUrls.has(normalizeAudioUrl(task.audioUrl))) {
        taskIdsToRemove.add(task.localId);
      }
    });

  tasks
    .filter((task) => task.status === 'completed' && !task.audioUrl)
    .sort((a, b) => a.createdAt - b.createdAt)
    .forEach((task) => {
      if (taskIdsToRemove.has(task.localId)) return;
      const replacement = remoteSoundEffects.find(
        (item) =>
          !task.initialRemoteSoundEffectIds.includes(item.soundEffectId) &&
          !consumedRemoteIds.has(item.soundEffectId)
      );
      if (!replacement) return;
      consumedRemoteIds.add(replacement.soundEffectId);
      taskIdsToRemove.add(task.localId);
    });

  return Array.from(taskIdsToRemove);
}
